import { FormatError } from 'intl-messageformat';
import {
  createIntl,
  createIntlCache,
  InvalidConfigError,
  MessageFormatError,
  MissingDataError,
  MissingTranslationError,
  ReactIntlErrorCode,
  UnsupportedFormatterError,
} from 'react-intl';
import { DEFAULT_INTL_CONFIG } from 'react-intl/src/utils';
import { supportedLocales } from './constants';

declare global {
  // eslint-disable-next-line no-var
  var __FS_INTL_METADATA__: {
    locale: string;
    messages: { [key: string]: string };
  };
}

/**
 * Util that executes a synchronous handshake with the ScriptSwap browser
 * extension to determine if it is active for the current tab.
 */
const isScriptSwapActive = (): boolean => {
  let scriptSwapActive = false;
  // Listen for successful handshake
  addEventListener(
    'SCRIPTSWAP_HANDSHAKE_SUCCESS' as keyof WindowEventMap,
    e => (scriptSwapActive = (e as CustomEvent<boolean>).detail ?? false),
    { once: true },
  );
  // Initialize handshake
  dispatchEvent(new CustomEvent('SCRIPTSWAP_HANDSHAKE'));
  return scriptSwapActive;
};

const onError = (
  error:
    | MissingTranslationError
    | MessageFormatError
    | MissingDataError
    | InvalidConfigError
    | UnsupportedFormatterError
    | FormatError,
) => {
  const isLocalDev = [
    'localhost',
    '127.0.0.1',
    'app.fullstory.test',
    'webber.internal.fullstory.test',
  ].includes(window.location.hostname);
  const scriptSwapActive = isScriptSwapActive();

  if (error.code == ReactIntlErrorCode.MISSING_TRANSLATION && (isLocalDev || scriptSwapActive)) {
    return;
  }
  DEFAULT_INTL_CONFIG.onError(error);
};

const cache = createIntlCache();
const intl = createIntlForLocale(
  window.__FS_INTL_METADATA__ ?? {
    locale: 'en',
    messages: {},
  },
);

function createIntlForLocale({ locale, messages }: typeof window.__FS_INTL_METADATA__) {
  const intl = createIntl(
    {
      locale,
      messages,
      onError,
    },
    cache,
  );
  return intl;
}

export { createIntlForLocale, intl, supportedLocales };
